import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import type { EligibilityKind } from '../../../../api/user/eligibility.api';
import { getAdminEligibility } from '../../../../api/admin/user.api';
import { ekycErrorMessage } from '../../../donate-goal/ekyc-flow';
import ExceptionNoteDialog from './exception-note-dialog';
import { planApproval } from './review-rules';

/**
 * Luồng bấm duyệt dùng chung cho yêu cầu mở donate và tích xanh creator:
 * tải điều kiện của tài khoản; thiếu mục bắt buộc thì báo và dừng; thiếu mục xét duyệt thì mở hộp
 * "Duyệt ngoại lệ" bắt ghi lý do; xong mới gọi `approve` (trang gọi tự xin mã Authenticator).
 */
export const useApprovalFlow = (
  kind: EligibilityKind,
  approve: (userId: string, note?: string) => Promise<void> | void
) => {
  const [pending, setPending] = useState<{ userId: string; labels: string[] } | null>(null);
  const approveRef = useRef(approve);
  approveRef.current = approve;
  // Bấm hai lần liền không tải điều kiện hai lần và không hỏi mã Authenticator hai lần.
  const inFlight = useRef(false);

  const start = async (userId: string) => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      let result;
      try {
        result = await getAdminEligibility(userId, kind);
      } catch (e) {
        toast.error(ekycErrorMessage(e, 'Cab chưa tải được điều kiện của tài khoản này.'));
        return;
      }
      const plan = planApproval(result);
      if (plan.kind === 'blocked') {
        toast.error(plan.message);
        return;
      }
      if (plan.kind === 'needNote') {
        setPending({ userId, labels: plan.labels });
        return;
      }
      await approveRef.current(userId);
    } finally {
      inFlight.current = false;
    }
  };

  const dialog = (
    <ExceptionNoteDialog
      open={!!pending}
      labels={pending?.labels ?? []}
      onCancel={() => setPending(null)}
      onSubmit={async (note) => {
        const id = pending?.userId;
        setPending(null);
        if (!id || inFlight.current) return;
        inFlight.current = true;
        try {
          await approveRef.current(id, note);
        } finally {
          inFlight.current = false;
        }
      }}
    />
  );

  return { start, dialog };
};
